import { useCallback, useEffect, useState } from 'react';
import { request, ApiError } from '../api/client';

interface Cadastro {
  id: string;
  telefone: string;
  status: string;
  nome: string | null;
  email: string | null;
  cpf: string | null;
  comprasOk: number;
  msgs: number;
  primeiraCompra: string | null;
}

interface Grupo {
  tipo: 'cpf' | 'nome' | 'app';
  chave: string;
  cadastros: Cadastro[];
  keeperSugeridoId: string | null;
  suspeitoSplit: boolean;
}

const ROTULO_TIPO: Record<Grupo['tipo'], string> = {
  cpf: 'Mesmo CPF',
  nome: 'Mesmo nome',
  app: 'Chat do app com o celular desta ficha',
};

interface Ignorado {
  id: string;
  tipo: string;
  chave: string;
  clienteId: string | null;
  motivo: string | null;
  criadoPor: string | null;
  createdAt: string;
}

/**
 * O CPF não é mais guardado por inteiro (desde 24/08/2026 — ver
 * `clients/cpf-anonimo.ts` no backend). O que chega aqui é
 * `anon:<hmac>:<3 dígitos>`, que serve pra AGRUPAR e não pra ler.
 *
 * A tela mostra só os 3 últimos dígitos: é o bastante pra reconhecer que dois
 * cadastros são o mesmo documento, que é a única pergunta desta página. O
 * `chave` cru continua indo e voltando na API porque é ele que identifica o
 * grupo no "ignorar" — trocá-lo faria os grupos já ignorados reaparecerem.
 */
function mostraDocumento(chave: string): string {
  const m = /^anon:[0-9a-f]{16}:([0-9]{3})$/.exec(chave);
  return m ? `•••.•••.••-${m[1]}` : 'sem documento';
}

function CadastroRow({
  c,
  keeper,
  onIgnorar,
}: {
  c: Cadastro;
  keeper: boolean;
  onIgnorar: () => void;
}) {
  return (
    <li
      className={`flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border px-3 py-2 text-sm ${
        keeper ? 'border-sara-accent bg-sara-bg-muted' : 'border-sara-border'
      }`}
    >
      <span className="font-medium text-sara-text">
        {keeper ? '★ ' : ''}
        {c.nome ?? '(sem nome)'}
      </span>
      <span className="text-sara-text-muted">{c.telefone}</span>
      <span
        className={
          c.status === 'bloqueada'
            ? 'text-sara-danger'
            : c.status === 'ativa'
              ? 'text-sara-success'
              : 'text-sara-text-subtle'
        }
      >
        {c.status}
      </span>
      <span className="text-sara-text-subtle">
        {c.comprasOk} compra(s) · {c.msgs} msg
      </span>
      {c.email && <span className="text-sara-text-subtle">{c.email}</span>}
      <button
        type="button"
        onClick={onIgnorar}
        title="Esse cadastro NÃO é a mesma pessoa — tira ele do grupo (permanente, reversível nos ignorados)"
        className="ml-auto text-xs text-sara-text-subtle underline hover:text-sara-danger"
      >
        não é a mesma pessoa
      </button>
    </li>
  );
}

export function DuplicatasPage() {
  const [grupos, setGrupos] = useState<Grupo[] | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [mesclando, setMesclando] = useState<string | null>(null);
  const [ignorados, setIgnorados] = useState<Ignorado[]>([]);
  const [mostrarIgnorados, setMostrarIgnorados] = useState(false);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      setGrupos(await request<Grupo[]>('/admin/clientes/duplicados'));
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : 'Falha ao carregar duplicatas');
    } finally {
      setCarregando(false);
    }
  }, []);

  const carregarIgnorados = useCallback(async () => {
    try {
      setIgnorados(await request<Ignorado[]>('/admin/clientes/duplicados/ignorados'));
    } catch {
      /* silencioso — seção secundária */
    }
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  useEffect(() => {
    if (mostrarIgnorados) void carregarIgnorados();
  }, [mostrarIgnorados, carregarIgnorados]);

  const ignorar = useCallback(
    async (payload: { tipo: string; chave: string; clienteId?: string }, confirmMsg: string) => {
      if (!window.confirm(confirmMsg)) return;
      setErro(null);
      try {
        await request('/admin/clientes/duplicados/ignorar', { method: 'POST', body: payload });
        await carregar();
        if (mostrarIgnorados) await carregarIgnorados();
      } catch (e) {
        setErro(e instanceof ApiError ? e.message : 'Falha ao ignorar');
      }
    },
    [carregar, carregarIgnorados, mostrarIgnorados],
  );

  const desfazerIgnore = useCallback(
    async (id: string) => {
      setErro(null);
      try {
        await request(`/admin/clientes/duplicados/ignorar/${id}`, { method: 'DELETE' });
        await Promise.all([carregar(), carregarIgnorados()]);
      } catch (e) {
        setErro(e instanceof ApiError ? e.message : 'Falha ao desfazer');
      }
    },
    [carregar, carregarIgnorados],
  );

  const mesclarGrupo = useCallback(
    async (g: Grupo) => {
      const keeper = g.cadastros.find((c) => c.id === g.keeperSugeridoId) ?? g.cadastros[0];
      if (!keeper) return;
      const ghosts = g.cadastros.filter((c) => c.id !== keeper.id);
      const ok = window.confirm(
        `Mesclar ${ghosts.length} cadastro(s) em "${keeper.nome ?? keeper.telefone}" (${keeper.telefone})?\n\n` +
          'As compras e mensagens vão pro cadastro que fica; os outros são removidos. Não pode ser desfeito pelo painel.',
      );
      if (!ok) return;
      setMesclando(g.chave);
      setErro(null);
      try {
        for (const ghost of ghosts) {
          await request('/admin/clientes/merge', {
            method: 'POST',
            body: { keeperId: keeper.id, ghostId: ghost.id },
          });
        }
        await carregar();
      } catch (e) {
        setErro(e instanceof ApiError ? e.message : 'Falha ao mesclar');
      } finally {
        setMesclando(null);
      }
    },
    [carregar],
  );

  return (
    <main className="space-y-4 p-6">
      <header>
        <h1 className="text-xl font-semibold text-sara-text">Duplicatas de clientes</h1>
        <p className="max-w-3xl text-sm text-sara-text-muted">
          Mesma pessoa em telefones diferentes: mesmo CPF, ou mesmo nome com um cadastro comprador +
          um sem compra (comprou num número, conversa noutro). <strong>Mesclar</strong> junta
          compras e mensagens no cadastro ★ (o com mais conversa) e remove os outros. Se não for pra
          mesclar, use <strong>“não é a mesma pessoa”</strong> (tira um cadastro) ou{' '}
          <strong>“descartar sugestão”</strong> (some com o grupo). Ambos são reversíveis nos
          ignorados.
        </p>
      </header>

      {erro && (
        <div
          role="alert"
          className="rounded-md border border-sara-danger px-3 py-2 text-sm text-sara-danger"
        >
          {erro}
        </div>
      )}

      {carregando && <p className="text-sm text-sara-text-muted">Carregando duplicatas…</p>}

      {!carregando && grupos && grupos.length === 0 && (
        <p className="text-sm text-sara-text-muted">Nenhuma duplicata encontrada. 🎉</p>
      )}

      {!carregando && grupos && grupos.length > 0 && (
        <>
          <p className="text-sm text-sara-text-subtle">{grupos.length} grupo(s) de duplicata.</p>
          <ul className="space-y-3">
            {grupos.map((g) => {
              const keeperId = g.keeperSugeridoId ?? g.cadastros[0]?.id;
              return (
                <li
                  key={`${g.tipo}:${g.chave}`}
                  className="rounded-lg border border-sara-border bg-sara-surface p-4"
                >
                  <div className="mb-2 flex items-center justify-between gap-3">
                    <span className="text-xs uppercase tracking-wide text-sara-text-subtle">
                      {ROTULO_TIPO[g.tipo]}
                      {g.suspeitoSplit && (
                        <span className="ml-2 rounded bg-sara-accent px-1.5 py-0.5 text-sara-accent-fg">
                          split de pagante
                        </span>
                      )}
                    </span>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() =>
                          void ignorar(
                            { tipo: g.tipo, chave: g.chave },
                            'Descartar essa sugestão inteira? Ela não vai mais aparecer (reversível nos ignorados).',
                          )
                        }
                        className="rounded-md border border-sara-border px-3 py-1.5 text-sm text-sara-text-muted hover:bg-sara-surface-hover"
                      >
                        Descartar sugestão
                      </button>
                      <button
                        type="button"
                        onClick={() => void mesclarGrupo(g)}
                        disabled={mesclando === g.chave}
                        className="rounded-md bg-sara-accent px-3 py-1.5 text-sm text-sara-accent-fg disabled:opacity-50"
                      >
                        {mesclando === g.chave ? 'Mesclando…' : 'Mesclar'}
                      </button>
                    </div>
                  </div>
                  <ul className="space-y-1.5">
                    {g.cadastros.map((c) => (
                      <CadastroRow
                        key={c.id}
                        c={c}
                        keeper={c.id === keeperId}
                        onIgnorar={() =>
                          void ignorar(
                            { tipo: g.tipo, chave: g.chave, clienteId: c.id },
                            `Marcar "${c.nome ?? c.telefone}" como NÃO sendo a mesma pessoa desse grupo?`,
                          )
                        }
                      />
                    ))}
                  </ul>
                </li>
              );
            })}
          </ul>
        </>
      )}

      <details
        className="rounded-lg border border-sara-border bg-sara-surface p-3 text-sm"
        onToggle={(e) => setMostrarIgnorados((e.target as HTMLDetailsElement).open)}
      >
        <summary className="cursor-pointer text-sara-text-muted">
          Sugestões ignoradas {ignorados.length > 0 ? `(${ignorados.length})` : ''}
        </summary>
        {mostrarIgnorados && ignorados.length === 0 && (
          <p className="mt-2 text-sara-text-subtle">Nenhuma sugestão ignorada.</p>
        )}
        {ignorados.length > 0 && (
          <ul className="mt-2 space-y-1.5">
            {ignorados.map((ig) => (
              <li
                key={ig.id}
                className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border border-sara-border px-3 py-2"
              >
                <span className="text-sara-text-subtle">
                  {ig.tipo === 'cpf' ? 'CPF' : ig.tipo === 'app' ? 'Celular do app' : 'Nome'}
                </span>
                <span className="font-mono text-xs text-sara-text-muted">
                  {ig.tipo === 'cpf' ? mostraDocumento(ig.chave) : ig.chave}
                </span>
                <span className="text-sara-text-subtle">
                  {ig.clienteId ? 'cadastro específico' : 'grupo inteiro'}
                </span>
                <button
                  type="button"
                  onClick={() => void desfazerIgnore(ig.id)}
                  className="ml-auto text-xs underline hover:text-sara-accent"
                >
                  desfazer
                </button>
              </li>
            ))}
          </ul>
        )}
      </details>
    </main>
  );
}
